import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getPasses, markArrived, markDeparted, markNoShow, getStats } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { 
  Shield, Sun, Moon, LogOut, User, Car, Search,
  LogIn, Clock, CheckCircle2, AlertTriangle, Users, Archive,
  Eye, Phone, FileText, CalendarIcon, ClipboardList, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate, Link } from 'react-router-dom';

export default function SecurityDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [passes, setPasses] = useState([]);
  const [stats, setStats] = useState({ total_active: 0, today_expected: 0, arrived: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activityLog, setActivityLog] = useState([]);
  const [selectedPass, setSelectedPass] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const loadData = useCallback(async () => {
    try {
      const [passesRes, statsRes] = await Promise.all([
        getPasses(false),
        getStats()
      ]);
      setPasses(passesRes.data);
      setStats(statsRes.data);
      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, [loadData]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const addToLog = (message, type = 'info') => {
    setActivityLog(prev => [{
      id: Date.now(),
      message,
      time: new Date(),
      type
    }, ...prev.slice(0, 19)]);
  };

  const handleArrive = async (pass) => {
    try {
      await markArrived(pass.id);
      const name = pass.pass_type === 'person' ? pass.person_data?.full_name : pass.vehicle_data?.license_plate;
      toast.success(`${name} отмечен как прибывший`);
      addToLog(`Прибытие: ${name}`, 'success');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDepart = async (pass) => {
    try {
      await markDeparted(pass.id);
      const name = pass.pass_type === 'person' ? pass.person_data?.full_name : pass.vehicle_data?.license_plate;
      toast.success(`${name} отмечен как выехавший`);
      addToLog(`Выезд: ${name}`, 'info');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleNoShow = async (pass) => {
    if (!window.confirm('Отметить как неявку? Заявка будет архивирована.')) return;
    try {
      await markNoShow(pass.id);
      const name = pass.pass_type === 'person' ? pass.person_data?.full_name : pass.vehicle_data?.license_plate;
      toast.success('Заявка отмечена как неявка');
      addToLog(`Неявка: ${name}`, 'warning');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const openDetails = (pass) => {
    setSelectedPass(pass);
    setDetailsOpen(true);
  };

  const filteredPasses = passes.filter(pass => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    
    // Common fields
    if (pass.created_by_name?.toLowerCase().includes(searchLower)) return true;
    if (pass.visit_date?.includes(search)) return true;
    if (pass.visit_time?.includes(search)) return true;
    
    if (pass.pass_type === 'person') {
      // Person fields
      if (pass.person_data?.full_name?.toLowerCase().includes(searchLower)) return true;
      if (pass.person_data?.passport_series?.includes(search)) return true;
      if (pass.person_data?.passport_number?.includes(search)) return true;
      if (pass.person_data?.visit_purpose?.toLowerCase().includes(searchLower)) return true;
    } else {
      // Vehicle fields
      if (pass.vehicle_data?.license_plate?.toLowerCase().includes(searchLower)) return true;
      if (pass.vehicle_data?.car_brand?.toLowerCase().includes(searchLower)) return true;
      if (pass.vehicle_data?.car_model?.toLowerCase().includes(searchLower)) return true;
      if (pass.vehicle_data?.contact_phone?.includes(search)) return true;
      if (pass.vehicle_data?.visit_purpose?.toLowerCase().includes(searchLower)) return true;
      if (pass.vehicle_data?.driver_name?.toLowerCase().includes(searchLower)) return true;
    }
    
    return false;
  });

  const today = format(new Date(), 'yyyy-MM-dd');
  const todayPasses = filteredPasses.filter(p => p.visit_date === today);
  const otherPasses = filteredPasses.filter(p => p.visit_date !== today);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-emerald-600">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-black tracking-tighter">SENTINEL</h1>
                <p className="text-xs text-muted-foreground">Пост охраны</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/permanent-passes">
                <Button variant="ghost" size="sm" className="gap-2" data-testid="permanent-passes-link">
                  <ClipboardList className="h-4 w-4" />
                  <span className="hidden sm:inline">Реестр</span>
                </Button>
              </Link>
              <Link to="/archive">
                <Button variant="ghost" size="sm" className="gap-2" data-testid="archive-link">
                  <Archive className="h-4 w-4" />
                  <span className="hidden sm:inline">Архив</span>
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">{user?.full_name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="animate-fade-in">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Всего активных</p>
                  <p className="text-3xl font-black">{stats.total_active}</p>
                </div>
                <Users className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="animate-fade-in" style={{ animationDelay: '0.05s' }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ожидается сегодня</p>
                  <p className="text-3xl font-black text-amber-500">{stats.today_expected}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">На территории</p>
                  <p className="text-3xl font-black text-emerald-500">{stats.arrived}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-emerald-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="animate-fade-in" style={{ animationDelay: '0.15s' }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ожидают</p>
                  <p className="text-3xl font-black text-blue-500">{stats.pending}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Active Queue */}
          <div className="lg:col-span-8">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <CardTitle>Активные заявки</CardTitle>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                      <RefreshCw className="h-3 w-3 animate-spin" style={{ animationDuration: '3s' }} />
                      <span>Обновлено: {format(lastUpdate, 'HH:mm:ss')}</span>
                    </div>
                  </div>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Поиск по имени или номеру..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[calc(100vh-400px)]">
                  {/* Today's passes */}
                  {todayPasses.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                        Сегодня ({format(new Date(), 'dd MMMM', { locale: ru })})
                      </h3>
                      <div className="space-y-3">
                        {todayPasses.map((pass, index) => (
                          <PassCard 
                            key={pass.id} 
                            pass={pass} 
                            index={index}
                            onArrive={handleArrive}
                            onDepart={handleDepart}
                            onNoShow={handleNoShow}
                            onViewDetails={openDetails}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Other passes */}
                  {otherPasses.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                        Другие даты
                      </h3>
                      <div className="space-y-3">
                        {otherPasses.map((pass, index) => (
                          <PassCard 
                            key={pass.id} 
                            pass={pass} 
                            index={index}
                            onArrive={handleArrive}
                            onDepart={handleDepart}
                            onNoShow={handleNoShow}
                            onViewDetails={openDetails}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {filteredPasses.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      {search ? 'Ничего не найдено' : 'Нет активных заявок'}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Activity Log */}
          <div className="lg:col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Журнал действий</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[calc(100vh-450px)]">
                  {activityLog.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Нет записей
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {activityLog.map(log => (
                        <div key={log.id} className="flex items-start gap-3 animate-slide-in">
                          <div className={`p-1.5 rounded-full mt-0.5 ${
                            log.type === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                            log.type === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' :
                            'bg-blue-100 dark:bg-blue-900/30'
                          }`}>
                            {log.type === 'success' ? (
                              <LogIn className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                            ) : log.type === 'warning' ? (
                              <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                            ) : (
                              <LogOut className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm">{log.message}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(log.time, 'HH:mm:ss')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Pass Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Детали заявки
            </DialogTitle>
          </DialogHeader>
          {selectedPass && (
            <div className="space-y-4">
              {/* Type indicator */}
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-md ${selectedPass.pass_type === 'person' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                  {selectedPass.pass_type === 'person' ? (
                    <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  ) : (
                    <Car className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-lg">
                    {selectedPass.pass_type === 'person' 
                      ? selectedPass.person_data?.full_name 
                      : selectedPass.vehicle_data?.license_plate}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedPass.pass_type === 'person' ? 'Посетитель' : 'Транспорт'}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Дата визита</p>
                  <p className="font-medium flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4" />
                    {format(new Date(selectedPass.visit_date), 'dd MMMM yyyy', { locale: ru })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Время визита</p>
                  <p className="font-medium flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {selectedPass.visit_time}
                  </p>
                </div>
              </div>

              {selectedPass.pass_type === 'person' ? (
                <div className="space-y-3">
                  {(selectedPass.person_data?.passport_series || selectedPass.person_data?.passport_number) && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedPass.person_data?.passport_series && (
                        <div>
                          <p className="text-muted-foreground">Серия паспорта</p>
                          <p className="font-mono font-medium">{selectedPass.person_data.passport_series}</p>
                        </div>
                      )}
                      {selectedPass.person_data?.passport_number && (
                        <div>
                          <p className="text-muted-foreground">Номер паспорта</p>
                          <p className="font-mono font-medium">{selectedPass.person_data.passport_number}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground text-sm">Цель визита</p>
                    <p className="font-medium">{selectedPass.person_data?.visit_purpose}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedPass.vehicle_data?.driver_name && (
                    <div>
                      <p className="text-muted-foreground text-sm">ФИО посетителя</p>
                      <p className="font-medium">{selectedPass.vehicle_data.driver_name}</p>
                    </div>
                  )}
                  {(selectedPass.vehicle_data?.car_brand || selectedPass.vehicle_data?.car_model) && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedPass.vehicle_data?.car_brand && (
                        <div>
                          <p className="text-muted-foreground">Марка</p>
                          <p className="font-medium">{selectedPass.vehicle_data.car_brand}</p>
                        </div>
                      )}
                      {selectedPass.vehicle_data?.car_model && (
                        <div>
                          <p className="text-muted-foreground">Модель</p>
                          <p className="font-medium">{selectedPass.vehicle_data.car_model}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground text-sm">Цель визита</p>
                    <p className="font-medium">{selectedPass.vehicle_data?.visit_purpose}</p>
                  </div>
                  {selectedPass.vehicle_data?.contact_phone && (
                    <div>
                      <p className="text-muted-foreground text-sm">Контактный телефон</p>
                      <p className="font-medium flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {selectedPass.vehicle_data.contact_phone}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <Separator />

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Заявитель</p>
                  <p className="font-medium">{selectedPass.created_by_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Создано</p>
                  <p className="font-medium">
                    {format(new Date(selectedPass.created_at), 'dd.MM.yyyy HH:mm')}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {selectedPass.notes && (
                <div className="p-3 rounded-md bg-muted/50">
                  <p className="text-muted-foreground text-sm mb-1">Примечание</p>
                  <p className="text-sm">{selectedPass.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                {selectedPass.status === 'pending' && (
                  <>
                    <Button
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => { handleArrive(selectedPass); setDetailsOpen(false); }}
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Прибыл
                    </Button>
                    <Button
                      variant="outline"
                      className="text-amber-600 border-amber-500"
                      onClick={() => { handleNoShow(selectedPass); setDetailsOpen(false); }}
                    >
                      Неявка
                    </Button>
                  </>
                )}
                {selectedPass.status === 'arrived' && (
                  <Button
                    className="flex-1"
                    variant="outline"
                    onClick={() => { handleDepart(selectedPass); setDetailsOpen(false); }}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Выехал
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PassCard({ pass, index, onArrive, onDepart, onNoShow, onViewDetails }) {
  const isPerson = pass.pass_type === 'person';
  const name = isPerson ? pass.person_data?.full_name : pass.vehicle_data?.license_plate;
  const details = isPerson 
    ? pass.person_data?.visit_purpose 
    : [pass.vehicle_data?.driver_name, pass.vehicle_data?.car_brand, pass.vehicle_data?.car_model].filter(Boolean).join(' • ') || pass.vehicle_data?.visit_purpose;

  return (
    <Card 
      className={`animate-fade-in transition-all hover:-translate-y-0.5 hover:shadow-md ${
        pass.status === 'pending' ? 'status-pending' : 
        pass.status === 'arrived' ? 'status-arrived' : 'status-departed'
      }`}
      style={{ animationDelay: `${index * 0.03}s` }}
      data-testid={`security-pass-${pass.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div 
            className="flex items-center gap-4 min-w-0 flex-1 cursor-pointer"
            onClick={() => onViewDetails(pass)}
          >
            <div className={`p-2 rounded-md flex-shrink-0 ${
              isPerson ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
            }`}>
              {isPerson ? (
                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              ) : (
                <Car className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              )}
            </div>
            <div className="min-w-0">
              <div className="font-semibold truncate">{name}</div>
              <div className="text-sm text-muted-foreground truncate">{details}</div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                <Clock className="h-3 w-3" />
                <span>{format(new Date(pass.visit_date), 'dd.MM')} в {pass.visit_time}</span>
                <span className="text-muted-foreground/50">•</span>
                <span>{pass.created_by_name}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onViewDetails(pass)}
              data-testid={`view-btn-${pass.id}`}
            >
              <Eye className="h-4 w-4" />
            </Button>
            {pass.status === 'pending' && (
              <>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => onArrive(pass)}
                  data-testid={`arrive-btn-${pass.id}`}
                >
                  <LogIn className="h-4 w-4 mr-1" />
                  Прибыл
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-amber-600 border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                  onClick={() => onNoShow(pass)}
                  data-testid={`noshow-btn-${pass.id}`}
                >
                  Неявка
                </Button>
              </>
            )}
            {pass.status === 'arrived' && (
              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={() => onDepart(pass)}
                data-testid={`depart-btn-${pass.id}`}
              >
                <LogOut className="h-4 w-4 mr-1" />
                Выехал
              </Button>
            )}
            {pass.status === 'departed' && (
              <Badge variant="outline" className="text-muted-foreground">Завершён</Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
